import { cloneDeep } from 'lodash';
import omitDeep from 'omit-deep-lodash';
import { deepDiffWithIgnores } from './Diff';
import type {
  App,
  AppInput,
  AppPriceInput,
  Brand,
  BrandInput,
  Menu,
  MenuInput,
  MenuOption,
  MenuOptionInput,
  OptionVariablesByApp,
  OptionVariablesByAppInput,
  Product,
  ProductInput,
  ProductVariablesByApp,
  ProductVariablesByAppInput,
  ProductVariant,
  Surcharge,
  SurchargeInput,
  VariantInput,
  VariantVariablesByApp
} from '../generated/graphql';

export type PosProductImageMap = {
  [posId: string]: string;
};

/**
 * Have any changes occurred in this product input?
 * Any defined properties besides POSId and variablesByApp represent a change to that field
 * Any defined properties within a variablesByApp object besides app represent a change
 * */
const productChangesDetected = (productInput: ProductInput): boolean => {
  const changesInVariablesByApps = productInput.variablesByApp.some((vba) => Object.keys(vba).length > 1);
  if (changesInVariablesByApps) return true;
  return Object.keys(productInput).length > 2;
};

/**
 * @desc Check if trimmed input is different to published field
 * @param input string field from input item
 * @param published string field from published item
 * @returns true if strings Not equal
 */
const stringPropHasChanged = (input: string, published: string): boolean => {
  return !(input === published);
};

/**
 *
 * @param input string field of input object (eg. product.description)
 * @param published string field of published object
 * @param emptySymbol if trimmed input field is empty, input this symbol in place
 * @returns `emptySymbol` if field removed, input if change detected, `undefined` if no real changes
 */
const generateStringLink = <T>(input: string, published: string, emptySymbol?: T): string | T | undefined => {
  if (!stringPropHasChanged(input, published)) return undefined;
  if (input.length === 0) return emptySymbol;
  return input;
};

/**
 * Will this app be modified by the changes in this product?
 * eg. A single price change to one specific app, will not affect another app
 * */
const secondaryAppAffectedByChange = (productInput: ProductInput, appId: string): boolean => {
  if (Object.keys(productInput).length > 2) return true;
  const vba = productInput.variablesByApp.find(({ app }) => app.id === appId);
  if (vba === undefined) return false;
  return vba.availability !== undefined || vba.price !== undefined;
};

// Omit __typename
const convertBrandToBrandInput = (value: Brand): BrandInput => {
  const { hashedId, name } = value;

  return { hashedId, name };
};

// Omit __typename
const convertAppToAppInput = (value: App): AppInput => {
  const { id, name, brand, image, internalAppId } = value;

  const brandInput: BrandInput | undefined = brand ? convertBrandToBrandInput(brand) : undefined;

  return { id, name, brand: brandInput, image, internalAppId };
};

// Omit item[i].__typename and item[i].surcharge
const convertProductVariablesByAppToProductVariablesByAppInput = (
  value: ProductVariablesByApp
): ProductVariablesByAppInput => {
  const { app, availability, price, imageUri } = value;

  const appInput: AppInput = convertAppToAppInput(app);

  return {
    app: appInput,
    availability,
    price,
    imageUri
  };
};

// Omit __typename
const convertOptionVariablesByAppToOptionVariablesByAppInput = (
  value: OptionVariablesByApp
): OptionVariablesByAppInput => {
  const { app, posId } = value;

  const appInput: AppInput = convertAppToAppInput(app);

  return {
    app: appInput,
    posId
  };
};

// Omit __typename
const convertSurchargeToSurchargeInput = (value: Surcharge): SurchargeInput => {
  const { amount, type } = value;

  return { amount, type };
};

// Omit __typename
const convertVariantVariablesByAppToAppPriceInput = (value: VariantVariablesByApp): AppPriceInput => {
  const { app, availability, price, surcharge } = value;
  const appInput: AppInput = convertAppToAppInput(app);

  return {
    app: appInput,
    availability,
    price,
    surcharge: surcharge ? convertSurchargeToSurchargeInput(surcharge) : undefined
  };
};

// Omit __typename, alternateNames, customPosId, price, mmId
const convertProductVariantToVariantInput = (value: ProductVariant): VariantInput => {
  const { name, posId, variablesByApp } = value;
  const appPriceInputs: AppPriceInput[] = variablesByApp.map<AppPriceInput>(
    convertVariantVariablesByAppToAppPriceInput
  );

  return {
    posId,
    name,
    variablesByApp: appPriceInputs
  };
};

// For property options, omit __typename, mmId, alternateNames
const convertOptionToOptionInput = (value: MenuOption): MenuOptionInput => {
  const { max, min, name, posId, variants, clonedPosId, customPosId, source, variablesByApp } = value;

  const optionsVariablesByAppInput: OptionVariablesByAppInput[] | undefined =
    variablesByApp?.map<OptionVariablesByAppInput>(convertOptionVariablesByAppToOptionVariablesByAppInput);

  const variantsInput: VariantInput[] | undefined = variants?.map<VariantInput>(convertProductVariantToVariantInput);

  return {
    max,
    min,
    name,
    posId,
    variants: variantsInput,
    variablesByApp: optionsVariablesByAppInput,
    clonedPosId,
    customPosId,
    source
  };
};

const convertProductToProductInput = (value: Product): ProductInput => {
  const {
    description,
    dietary,
    includedItems,
    menuDir,
    name,
    options,
    posId,
    surcounts,
    tags,
    variablesByApp,
    alternateNames,
    bundledItems,
    customPosId,
    imageUri,
    type
  } = value;

  // For property variablesByApp, omit item[i].__typename and item[i].surcharge
  const variablesByAppInput: ProductVariablesByAppInput[] = variablesByApp.map(
    (variablesByAppItem): ProductVariablesByAppInput => {
      return convertProductVariablesByAppToProductVariablesByAppInput(variablesByAppItem);
    }
  );

  const optionsInput: MenuOptionInput[] = options.map<MenuOptionInput>(convertOptionToOptionInput);

  // Omit __typename, mmId
  return {
    description,
    dietary,
    includedItems,
    menuDir,
    name,
    options: optionsInput,
    posId,
    surcounts,
    tags,
    variablesByApp: variablesByAppInput,
    alternateNames,
    bundledItems,
    customPosId,
    imageUri,
    type
  };
};

export const convertToProductInput = (
  productWithDraft: Product,
  publishedProduct?: Product,
  newlyAddedPosProducts?: Product[],
  posProductImageUri?: string
): ProductInput => {
  const clonedProductWithDraft = cloneDeep(productWithDraft);
  const clonedPublishedProduct = cloneDeep(publishedProduct);
  const clonedNewlyAddedPosProduct = cloneDeep(
    (newlyAddedPosProducts || []).find((p) =>
      p.customPosId ? p.customPosId === clonedProductWithDraft.customPosId : p.posId === clonedProductWithDraft.posId
    )
  );
  const initialProduct = clonedPublishedProduct || clonedNewlyAddedPosProduct;
  // Remove locally added images
  clonedProductWithDraft.variablesByApp.forEach((vba) => {
    if (vba.imageUri?.startsWith('blob')) {
      vba.imageUri = null;
    }
  });

  if (!initialProduct) {
    return convertProductToProductInput(clonedProductWithDraft);
  }

  const { posId, name, dietary, description, variablesByApp: modifiedVariablesByApp, options } = clonedProductWithDraft;

  const updatedOptions: MenuOptionInput[] =
    options &&
    options.map<MenuOptionInput>((option) => {
      const initialOption = initialProduct.options.find((initialOption) => initialOption.posId === option.posId);
      if (!initialOption || option.clonedPosId) {
        // return option;
        return convertOptionToOptionInput(option);
      }

      const { posId, name, min, max, source, variants, customPosId, clonedPosId } = option;

      const updatedVariants = variants?.map<VariantInput>((variant) => {
        const initialVariant = initialOption.variants?.find((initialVariant) => initialVariant.posId === variant.posId);

        const { posId, name, variablesByApp: modifiedVariablesByApp } = variant;

        const variablesByApp = modifiedVariablesByApp.map<AppPriceInput>((mpa) => {
          const { app, price } = mpa;
          const initialVariablesByApp = initialVariant?.variablesByApp.find((ipa) => ipa.app.id === app.id);
          return {
            app: convertAppToAppInput(app),
            ...(price !== undefined && price !== initialVariablesByApp?.price && { price })
          };
        });

        return {
          posId,
          variablesByApp,
          ...(name && name !== initialVariant?.name && { name })
        };
      });

      return {
        posId,
        ...(source && { source }),
        ...(name && name !== initialOption.name && { name }),
        ...(min !== undefined && min !== initialOption.min && { min }),
        ...(max !== undefined && max !== initialOption.max && { max }),
        ...(customPosId && { customPosId }),
        ...(clonedPosId && { clonedPosId }),
        ...(variants && deepDiffWithIgnores(variants, initialOption.variants).length && { variants: updatedVariants })
      };
    });

  const variablesByApp = modifiedVariablesByApp.reduce<ProductVariablesByAppInput[]>((acc, cur) => {
    if (clonedProductWithDraft.mmId.includes('doshiinewapp') && cur.surcharge && cur.surcharge.amount > 0) {
      return acc;
    }

    const { app, availability, price, imageUri } = cur;
    const initialVariableByApp = initialProduct.variablesByApp.find(
      (initialProductVariablesByAppItem) => initialProductVariablesByAppItem.app.id === app.id
    );

    const nextItem: ProductVariablesByAppInput = {
      app: {
        id: app.id,
        name: app.name,
        brand: app.brand ? convertBrandToBrandInput(app.brand) : undefined,
        image: app.image,
        ...(app.internalAppId && { internalAppId: app.internalAppId })
      },
      ...(availability && availability !== initialVariableByApp?.availability && { availability }),
      ...(price !== undefined && price !== initialVariableByApp?.price && { price }),
      // Set imageUri if:
      ...(imageUri &&
        imageUri !== initialVariableByApp?.imageUri &&
        // ...there is no posProductImageUri
        (!posProductImageUri ||
          // ...the new imageUri is not the same as posProductImageUri
          imageUri !== posProductImageUri) && { imageUri })
    };

    acc.push(nextItem);

    return acc;
  }, []);

  const draftProduct: ProductInput = {
    posId,
    variablesByApp,
    ...(name && name !== initialProduct.name && { name }),
    ...(dietary && deepDiffWithIgnores(dietary, initialProduct.dietary).length && { dietary }),
    description: generateStringLink(description, initialProduct.description, ' '),
    ...(options &&
      deepDiffWithIgnores(options, initialProduct.options, ['source']).length && { options: updatedOptions })
  };
  return draftProduct;
};

export const convertToMenuInput = (
  menuWithDraft: Menu,
  publishedMenu?: Menu,
  newlyAddedPosProducts?: Product[],
  newlyAddedPosProductImageMap?: PosProductImageMap
): MenuInput => {
  const clonedMenuWithDraft = cloneDeep(menuWithDraft);
  const clonedPublishedMenu = cloneDeep(publishedMenu);
  const filteredClonedMenuWithDraft = clonedMenuWithDraft.categories.filter(
    (category) => category.mmId !== 'UNCATEGORISED'
  );
  const draftMenu = {
    apps: menuWithDraft.apps,
    categories: filteredClonedMenuWithDraft.map((categoryWithDraft) => {
      const initialCategory = clonedPublishedMenu?.categories.find(
        (publishedCategory) => publishedCategory.mmId === categoryWithDraft.mmId
      );
      if (!initialCategory) return categoryWithDraft;

      const { idsByApp, category: categoryName, displayOrder, productIds, imageUri, description } = categoryWithDraft;

      return {
        idsByApp,
        ...(categoryName && categoryName !== initialCategory.category && { category: categoryName }),
        ...(displayOrder && displayOrder !== initialCategory.displayOrder && { displayOrder }),
        ...(productIds && deepDiffWithIgnores(productIds, initialCategory.productIds).length && { productIds }),
        ...(imageUri && imageUri !== initialCategory.imageUri && { imageUri }),
        ...(description && description !== initialCategory.description && { description })
      };
    }),
    products: clonedMenuWithDraft.products.map((p) => {
      const publishedProduct = clonedPublishedMenu?.products.find(
        (publishedProduct) => publishedProduct.mmId === p.mmId
      );
      return convertToProductInput(p, publishedProduct, newlyAddedPosProducts, newlyAddedPosProductImageMap?.[p.posId]);
    })
  };
  return omitDeep(draftMenu, ['__typename', 'alternateNames', 'mmId']) as MenuInput;
};

export const compileChanges = (
  menuInput: MenuInput,
  publishedMenu: Menu | null
): { productPosIdsModified: string[]; otherAppsAffected: App[]; isMenuModified: boolean } => {
  let isMenuModified = false;
  const { productPosIdsModified, otherAppsAffected } = menuInput.products
    ? menuInput.products.reduce(
        (acc: { productPosIdsModified: string[]; otherAppsAffected: App[] }, currentProductInput) => {
          // If the product has been edited (does not have posID and variablesByApp ONLY as keys)
          if (productChangesDetected(currentProductInput)) {
            isMenuModified = true;
            acc.productPosIdsModified.push(currentProductInput.posId);
            currentProductInput.variablesByApp.forEach((variableByApp) => {
              // Add app to otherApps affected only if it is affected by change
              if (
                !menuInput.apps.some((app) => app.id === variableByApp.app.id) &&
                !acc.otherAppsAffected.some((app) => app.id === variableByApp.app.id) &&
                secondaryAppAffectedByChange(currentProductInput, variableByApp.app.id)
              ) {
                acc.otherAppsAffected.push(variableByApp.app);
              }
            });
          }
          return acc;
        },
        { productPosIdsModified: [], otherAppsAffected: [] }
      )
    : { productPosIdsModified: [], otherAppsAffected: [] };

  publishedMenu?.products.forEach((product) => {
    if (!menuInput.products?.some((p) => p.posId === product.posId)) {
      isMenuModified = true;
      product.variablesByApp.forEach((variableByApp) => {
        if (
          !menuInput.apps.some((app) => app.id === variableByApp.app.id) &&
          !otherAppsAffected.some((app) => app.id === variableByApp.app.id)
        ) {
          otherAppsAffected.push(variableByApp.app);
        }
      });
    }
  });
  menuInput.categories?.forEach((category) => {
    if (Object.keys(category).length !== 1) isMenuModified = true;
  });
  return {
    productPosIdsModified,
    otherAppsAffected,
    isMenuModified
  };
};

export const getChangedOptionsInProduct = (
  oldProductOptions: MenuOption[],
  newProductOptions: MenuOption[],
  knownNewOptions: MenuOption[]
): [changedOptions: MenuOption[], newOptions: MenuOption[]] => {
  const editedOptionGroups: MenuOption[] = [];

  // remove any potentially deleted options from knownNewOptions.
  const newOptions = knownNewOptions.filter((newOp) =>
    newProductOptions.some((currOp) => currOp.posId === newOp.posId)
  );
  newProductOptions.forEach((option) => {
    // Check If new Option differs from old option
    const oldOption = oldProductOptions.find((oldOption: MenuOption) => oldOption.posId === option.posId);
    if (oldOption) {
      const diffDetected = !!deepDiffWithIgnores(option, oldOption, ['mmId']).length;
      if (diffDetected) {
        editedOptionGroups.push(option);
      }
      return;
    }
    // Check If new Option differs from old version of new option
    const existingNewOption = newOptions.find(
      (existingNewOption: MenuOption) => existingNewOption.posId === option.posId
    );
    if (!existingNewOption) {
      newOptions.push(option);
      return;
    }
    const diffDetected = !!deepDiffWithIgnores(option, existingNewOption, ['mmId']).length;
    if (diffDetected) {
      // Don't want duplicate entries between the two arrays
      editedOptionGroups.push(option);
      const index = newOptions.indexOf(existingNewOption);
      if (index > -1) {
        newOptions.splice(index, 1);
      }
    }
  });
  return [editedOptionGroups, newOptions];
};

/**
 * Very basic check for null prices for products
 * @date 17/10/2023 - 09:15:17
 * @param {Product[]} products - A list of Product Inputs to check
 * @return {Product[]} all products with null prices
 * */
export const validateProductInputs = (products: Product[]): Product[] => {
  return products.filter(({ variablesByApp }) => {
    return variablesByApp.some((vba) => {
      return Number.isNaN(vba.price);
    });
  });
};

const constructBareProductLink = (productInput: ProductInput, apps: AppInput[]): ProductInput => {
  return {
    posId: productInput.posId,
    customPosId: productInput.customPosId,
    variablesByApp: apps.map((app) => ({ app }))
  };
};

/**
 * @description Create product inputs to be used to simply link a product to an app with no updates.
 * @description This linking step is required so that images updates can occur before applying all other updates + "app_menu_published" notification.
 * @description TODO Refactor b/e notification + record/audit creation into separate endpoint to allow (combined linking/updating -> image update), rather than (link -> image update -> link again)
 * @param productInputs[]
 * @param apps[]
 */
export const constructBareProductLinks = (productInputs: ProductInput[], apps: AppInput[]): ProductInput[] => {
  return productInputs.map((p) => constructBareProductLink(p, apps));
};
